import React from 'react';
import PropTypes from 'prop-types';
import Button from 'react-bootstrap/lib/Button';
import deepmerge from 'deepmerge';
import { get, set, cloneDeep } from 'lodash';
import i18n from '../../../src/i18n/translate';
import Row from '../Row';
import Icon from '../Icon';

export default class MultiRowFieldGroup extends React.Component {
  static propTypes = {
    name: PropTypes.string,
    idField: PropTypes.string,
    data: PropTypes.object,
    dataValue: PropTypes.array,
    children: PropTypes.any,
    className: PropTypes.string,
    runValidation: PropTypes.bool,
    onValueChange: PropTypes.func,
    onMultiValueChange: PropTypes.func,
    valueChangeDelay: PropTypes.number,
    scrollContainerId: PropTypes.string,
    hasDeleteButton: PropTypes.bool,
    hasAddButton: PropTypes.bool,
    deleteButton: PropTypes.object,
    addButton: PropTypes.object,
    rowWrapper: PropTypes.func,
    rowClassName: PropTypes.string,
    rowButtonsClassName: PropTypes.string,
    rowButtons: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.func
      ])
    ),
    newObject: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.func
    ]),
    deleteAction: PropTypes.func,
    addAction: PropTypes.func,
    activeLanguage: PropTypes.object,
    languages: PropTypes.arrayOf(PropTypes.object),
    disabled: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
    canAdd: PropTypes.func,
    canDelete: PropTypes.func
  };

  static defaultProps = {
    idField: "id",
    min: 1,
    max: -1,
    hasDeleteButton: true,
    hasAddButton: true
  };

  constructor(props) {
    super(props);

    this.state = {
      dataValue: []
    };

    this.nextKey = 1;
  }

  componentWillMount() {
    this.syncDataValue(this.props);
  }

  syncDataValue(props) {
    let dataValue = props.data ? get(props.data, props.name) : props.dataValue;
    let prevCount;
    if (!dataValue) {
      dataValue = [];
      prevCount = -1;
    } else {
      prevCount = dataValue.length;
    }
    while (dataValue.length < props.min) {
      dataValue.push(this.createNewValue(dataValue.length));
    }
    if (dataValue.length !== prevCount) {
      this.doValueChange([...dataValue]);
    }
    this.nextKey = dataValue ? dataValue.length + 1 : 1;
    this.setState({ dataValue });
  }

  delete = (index) => {
    return () => {
      if (!this.canDeleteRow(index)) {
        return;
      }
      const deletedValue = this.state.dataValue[index];
      if (typeof this.props.deleteAction === 'function') {
        this.props.deleteAction(deletedValue, index);
      }
      let deletedValues = [];
      if (deletedValue[this.props.idField]) {
        deletedValues.push({ ...deletedValue, _destroy: true });
      }
      this.doValueChange([
        ...this.state.dataValue.slice(0, index),
        ...deletedValues,
        ...this.state.dataValue.slice(index + 1)
      ]);
    }
  };

  add = () => {
    if (!this.canAddRow()) {
      return;
    }
    const index = this.state.dataValue.length;
    const newValue = this.createNewValue(index);
    if (typeof this.props.addAction === 'function') {
      this.props.addAction(newValue, index);
    }
    this.doValueChange([
      ...this.state.dataValue,
      newValue
    ]);
  };

  createNewValue = index => {
    let newValue = this.newValue(index);
    newValue._key = this.nextKey++;
    return newValue;
  };

  onValueChange = () => {
    return (obj) => {
      obj = Object.entries(obj).reduce((newObj, [key, value]) => {
        set(newObj, key, value);
        return newObj;
      }, {});
      this.doValueChange(deepmerge(this.state.dataValue, obj[this.props.name]));
    }
  };

  doValueChange = (dataValue) => {
    this.setState({ dataValue });
    const { onValueChange, onMultiValueChange } = this.props;
    this.callValueChange(dataValue, onValueChange);
    this.callValueChange(dataValue, onMultiValueChange);
  };

  callValueChange = (dataValue, onValueChange) => {
    const { name } = this.props;
    if (typeof onValueChange === 'function') {
      if (name) {
        onValueChange({ [name]: dataValue });
      } else {
        onValueChange(dataValue);
      }
    }
  }

  newValue(index) {
    if (typeof this.props.newObject === 'function') {
      return this.props.newObject(index);
    } else if (this.props.newObject) {
      return cloneDeep(this.props.newObject);
    } else {
      return {};
    }
  }

  get activeRows() {
    return (this.state.dataValue || []).filter(row => !row._destroy);
  }

  canAddRow() {
    if (typeof this.props.canAdd === 'function') {
      if (!this.props.canAdd(this.activeRows.length)) {
        return false;
      }
    }
    return this.props.max <= 0 || this.activeRows.length < this.props.max;
  }

  canDeleteRow(index) {
    if (typeof this.props.canDelete === 'function') {
      if (!this.props.canDelete(this.state.dataValue[index], index)) {
        return false;
      }
    }
    return this.activeRows.length > this.props.min;
  }

  renderButton = (props, index, key) => {
    if (typeof props === 'function') {
      return this.renderButton(props(index), index, key);
    }
    if (props && props.action) { // Object for simple button
      return (
        <Button
          bsStyle={props.className || 'secondary'}
          onClick={props.action}
          disabled={this.props.disabled || props.disabled}
          key={key}>
          { props.icon ? <Icon iconType={props.icon} /> : null }
          { props.label }
        </Button>
      )
    } else {
      return null;
    }
  };

  renderChild = (index) => {
    if (!this.props.children) {
      return null;
    }
    return React.Children.map(this.props.children, (child) => {
      if (child) {
        let name;
        if (this.props.name) {
          let namePrefix = `${this.props.name}[${index}]`;
          name = child.props.name;
          if (name) {
            name = `${namePrefix}[${name}]`;
          } else {
            name = namePrefix;
          }
        }

        return React.cloneElement(child, {
          data: this.props.data || this.props.dataValue[index],
          extractedData: this.props.dataValue ? this.props.dataValue[index]: null,
          onValueChange: this.onValueChange(index),
          valueChangeDelay: this.props.valueChangeDelay,
          runValidation: this.props.runValidation,
          scrollContainerId: this.props.scrollContainerId,
          activeLanguage: this.props.activeLanguage,
          languages: this.props.languages,
          disabled: this.props.disabled,
          name: name || child.props.name
        });
      }
    }, this);
  };

  renderRow = (value, index) => {
    let child = this.renderChild(index);
    if (typeof this.props.rowWrapper === 'function') {
      child = this.props.rowWrapper(child, value, index);
    }
    let rowButtons = this.props.rowButtons || [];
    if (this.props.hasDeleteButton) {
      rowButtons = [...rowButtons,
        Object.assign({}, {
          label: i18n.t('Delete'),
          className: 'link btn-multi-delete',
          icon: 'zmdi zmdi-delete'
        }, this.props.deleteButton, {
          action: this.delete(index),
          disabled: !this.canDeleteRow(index)
        })
      ];
    }
    rowButtons = rowButtons.map((rowButton, buttonIndex) => this.renderButton(rowButton, index, buttonIndex));
    const key = value._key || index;
    return <Row key={`r_${key}`}
      rowClassName={this.props.rowClassName}
      rowButtonsClassName={this.props.rowButtonsClassName}
      rowButtons={rowButtons}
      disabled={this.props.disabled}>
      {child}
    </Row>;
  };

  render() {
    return (<div className={this.props.className}>
      {
        (this.state.dataValue || []).map((value, index) => {
          if (value._destroy) {
            return null;
          }
          return this.renderRow(value, index);
        })
      }
      {
        this.props.hasAddButton ?
          <div className="row">
            <div className="col-12">
              {
                this.renderButton(
                  Object.assign({}, {
                    label: i18n.t('Add'),
                    className: 'secondary btn-addbutton',
                    icon: 'zmdi zmdi-plus'
                  }, this.props.addButton, {
                    action: this.add,
                    disabled: !this.canAddRow()
                  })
                )
              }
            </div>
          </div> :
          null
      }
    </div>);
  }
}
